import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const NUMERIC_SET_ACTIONS = {
  modify_set_reps: { field: 'repetitions', argument: 'new_reps' },
  modify_set_weight: { field: 'weight', argument: 'new_weight' },
};
const finiteNumber = value => typeof value === 'number' && Number.isFinite(value);

export function buildCoachAppliedTargets(actions = [], response = {}) {
  const results = Array.isArray(response?.results) ? response.results : null;
  const resultById = new Map((results || []).map(result => [String(result?.action_id || ''), result]));
  return (Array.isArray(actions) ? actions : []).flatMap(action => {
    const shape = NUMERIC_SET_ACTIONS[action?.tool_name];
    const result = results ? resultById.get(String(action?.action_id || '')) : null;
    const applied = results ? result?.success === true && result?.terminal === true && result?.status === 'approved' && result?.already_applied !== true
      : response?.success === true && response?.already_applied !== true
        && response?.terminal !== false && (!response?.status || response.status === 'approved');
    const expectedValue = shape ? action?.arguments?.[shape.argument] : undefined;
    const workoutExerciseId = action?.arguments?.workout_exercise_id;
    const setId = action?.arguments?.set_id;
    if (!shape || !action?.action_id || !applied || !workoutExerciseId || !setId || !finiteNumber(expectedValue)) return [];
    return [{ actionId: action.action_id, workoutExerciseId, setId, field: shape.field, expectedValue }];
  });
}

function canonicalValue(exercises, target) {
  const exercise = (Array.isArray(exercises) ? exercises : []).find(item => String(item?._id) === String(target.workoutExerciseId));
  return exercise?.sets?.find(item => String(item?._id) === String(target.setId))?.[target.field];
}

export function reconcileCoachAppliedTargets(targets = [], beforeExercises = [], afterExercises = []) {
  return targets.filter(target => {
    const before = canonicalValue(beforeExercises, target);
    const after = canonicalValue(afterExercises, target);
    return finiteNumber(before) && finiteNumber(after) && before !== after && after === target.expectedValue;
  });
}

export function coachFieldEffectClass(effects, workoutExerciseId, setId, field) {
  const match = effects?.targets?.find(target => String(target.workoutExerciseId) === String(workoutExerciseId)
    && String(target.setId) === String(setId) && target.field === field);
  const generation = match?.generation ?? effects?.generation;
  return match ? ` coach-applied-field-effect coach-applied-field-effect-${generation % 2 ? 'odd' : 'even'}` : '';
}

const targetKey = target => [
  target?.actionId,
  target?.workoutExerciseId,
  target?.setId,
  target?.field,
].map(value => String(value ?? '')).join('|');
const fieldKey = target => [
  target?.workoutExerciseId,
  target?.setId,
  target?.field,
].map(value => String(value ?? '')).join('|');

export function useCoachAppliedFieldEffects(scope) {
  const scopeKey = String(scope ?? '');
  const [storedEffects, setStoredEffects] = useState({ scopeKey, generation: 0, targets: [] });

  useEffect(() => {
    setStoredEffects(current => current.scopeKey === scopeKey
      ? current
      : { scopeKey, generation: current.generation, targets: [] });
  }, [scopeKey]);

  const publish = useCallback(targets => {
    if (!Array.isArray(targets) || targets.length === 0) return;
    setStoredEffects(current => ({
      scopeKey,
      generation: current.generation + 1,
      targets,
    }));
  }, [scopeKey]);

  const markDisplayed = useCallback(displayed => {
    setStoredEffects(current => {
      if (current.scopeKey !== scopeKey || current.generation !== displayed?.generation) return current;
      const key = targetKey(displayed);
      const targets = current.targets.filter(target => targetKey(target) !== key);
      return targets.length === current.targets.length ? current : { ...current, targets };
    });
  }, [scopeKey]);

  const effects = storedEffects.scopeKey === scopeKey
    ? storedEffects
    : { scopeKey, generation: storedEffects.generation, targets: [] };
  return { effects, publish, markDisplayed };
}

function displayedValue(exercises, target) {
  const exercise = exercises.find(item => String(item?._id) === String(target.workoutExerciseId));
  return exercise?.sets?.find(set => String(set?._id) === String(target.setId))?.[target.field];
}

export function useDisplayedCoachAppliedFieldEffects({ effects, displayedExercises = [], onDisplayed }) {
  const scopeKey = String(effects?.scopeKey ?? '');
  const [activeTargets, setActiveTargets] = useState([]);
  const timersRef = useRef(new Map());
  const consumedRef = useRef(new Set());

  useEffect(() => {
    for (const timer of timersRef.current.values()) clearTimeout(timer);
    timersRef.current.clear();
    consumedRef.current.clear();
    setActiveTargets([]);
  }, [scopeKey]);

  useEffect(() => {
    const readyTargets = (effects?.targets || []).filter(target => (
      displayedValue(displayedExercises, target) === target.expectedValue
    ));
    for (const target of readyTargets) {
      const renderedFieldKey = fieldKey(target);
      const deliveryKey = `${scopeKey}|${effects.generation}|${targetKey(target)}`;
      if (consumedRef.current.has(deliveryKey)) continue;
      consumedRef.current.add(deliveryKey);

      const previousTimer = timersRef.current.get(renderedFieldKey);
      if (previousTimer) clearTimeout(previousTimer);
      const activeTarget = { ...target, generation: effects.generation, scopeKey };
      setActiveTargets(current => [
        ...current.filter(item => fieldKey(item) !== renderedFieldKey),
        activeTarget,
      ]);
      onDisplayed?.({ ...target, generation: effects.generation, scopeKey });
      const timer = setTimeout(() => {
        setActiveTargets(current => current.filter(item => (
          fieldKey(item) !== renderedFieldKey || item.generation !== effects.generation || item.scopeKey !== scopeKey
        )));
        if (timersRef.current.get(renderedFieldKey) === timer) timersRef.current.delete(renderedFieldKey);
      }, 1450);
      timersRef.current.set(renderedFieldKey, timer);
    }
  }, [displayedExercises, effects, onDisplayed, scopeKey]);

  useEffect(() => {
    setActiveTargets(current => {
      let changed = false;
      const targets = current.filter(target => {
        if (displayedValue(displayedExercises, target) === target.expectedValue) return true;
        changed = true;
        const renderedFieldKey = fieldKey(target);
        const timer = timersRef.current.get(renderedFieldKey);
        if (timer) clearTimeout(timer);
        timersRef.current.delete(renderedFieldKey);
        return false;
      });
      return changed ? targets : current;
    });
  }, [displayedExercises]);

  useEffect(() => () => {
    for (const timer of timersRef.current.values()) clearTimeout(timer);
    timersRef.current.clear();
  }, []);

  return useMemo(() => ({
    scopeKey,
    generation: effects?.generation || 0,
    targets: activeTargets.filter(target => target.scopeKey === scopeKey),
  }), [activeTargets, effects?.generation, scopeKey]);
}
